import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Phone, BarChart3, Shield, Trash2, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    whatsapp_number: '',
    phone: '',
    email: '',
    gtm_id: '',
    meta_pixel_id: '',
    ga4_id: '',
    retention_days: 90,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningRetention, setRunningRetention] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/settings');
        setSettings({
          whatsapp_number: res.data.whatsapp_number || '',
          phone: res.data.phone || '',
          email: res.data.email || '',
          gtm_id: res.data.gtm_id || '',
          meta_pixel_id: res.data.meta_pixel_id || '',
          ga4_id: res.data.ga4_id || '',
          retention_days: res.data.retention_days || 90,
        });
        const log = await api.get('/admin/audit-log?limit=20');
        setAuditLog(log.data || []);
      } catch {
        toast.error('Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Einstellungen gespeichert.');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const runRetention = async () => {
    if (!window.confirm(`Wirklich alle inaktiven Daten älter als ${settings.retention_days} Tage löschen?`)) return;
    setRunningRetention(true);
    try {
      const res = await api.post('/admin/retention/run');
      toast.success(`Bereinigt: ${res.data.leads_removed} Leads, ${res.data.chats_removed} Chat-Sessions`);
      const log = await api.get('/admin/audit-log?limit=20');
      setAuditLog(log.data || []);
    } catch {
      toast.error('Fehler beim Ausführen der Löschroutine');
    } finally {
      setRunningRetention(false);
    }
  };

  if (loading) {
    return <AdminLayout title="Einstellungen"><div className="text-center py-12 text-gray-500">Wird geladen...</div></AdminLayout>;
  }

  return (
    <AdminLayout title="Einstellungen">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kontakt */}
        <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="settings-contact">
          <h3 className="font-semibold text-[#4A331D] mb-4 flex items-center gap-2">
            <Phone className="w-4 h-4" /> Kontaktdaten
          </h3>
          <div className="space-y-4">
            <Field id="whatsapp" label="WhatsApp Nummer" value={settings.whatsapp_number}
              onChange={(v) => setSettings({ ...settings, whatsapp_number: v })}
              placeholder="4917620283482" hint="Ländervorwahl + Nummer ohne Leerzeichen" testid="settings-whatsapp" />
            <Field id="phone" label="Telefonnummer" value={settings.phone}
              onChange={(v) => setSettings({ ...settings, phone: v })}
              placeholder="4917620283482" testid="settings-phone" />
            <Field id="email" label="E-Mail Adresse" type="email" value={settings.email}
              onChange={(v) => setSettings({ ...settings, email: v })}
              placeholder="kontakt@bekajbau.de" testid="settings-email" />
          </div>
        </div>

        {/* Tracking */}
        <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="settings-tracking">
          <h3 className="font-semibold text-[#4A331D] mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Tracking (Consent Mode v2)
          </h3>
          <div className="space-y-4">
            <Field id="gtm_id" label="Google Tag Manager ID" value={settings.gtm_id}
              onChange={(v) => setSettings({ ...settings, gtm_id: v })}
              placeholder="GTM-XXXXXXX" hint="Lädt erst nach Cookie-Einwilligung" testid="settings-gtm" />
            <Field id="ga4_id" label="Google Analytics 4 (GA4) ID" value={settings.ga4_id}
              onChange={(v) => setSettings({ ...settings, ga4_id: v })}
              placeholder="G-XXXXXXXXXX" hint="Optional, eigenständige GA4-Messung" testid="settings-ga4" />
            <Field id="meta_pixel_id" label="Meta Pixel ID" value={settings.meta_pixel_id}
              onChange={(v) => setSettings({ ...settings, meta_pixel_id: v })}
              placeholder="123456789012345" hint="Facebook / Instagram" testid="settings-pixel" />
          </div>
        </div>

        {/* DSGVO / Retention */}
        <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="settings-retention">
          <h3 className="font-semibold text-[#4A331D] mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Datenschutz & Aufbewahrung
          </h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="retention_days">Aufbewahrungsdauer (Tage)</Label>
              <Input
                id="retention_days"
                type="number"
                min={7}
                max={3650}
                value={settings.retention_days}
                onChange={(e) => setSettings({ ...settings, retention_days: parseInt(e.target.value || '0', 10) })}
                className="mt-1"
                data-testid="settings-retention"
              />
              <p className="text-xs text-gray-500 mt-1">
                Inaktive Leads & Chat-Sessions werden nach dieser Anzahl Tagen automatisch gelöscht (täglich).
                Abgeschlossene Aufträge werden NICHT automatisch entfernt.
              </p>
            </div>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={runRetention}
              disabled={runningRetention}
              data-testid="run-retention-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {runningRetention ? 'Wird ausgeführt...' : 'Löschroutine jetzt manuell ausführen'}
            </Button>
          </div>
        </div>

        {/* Audit Log */}
        <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="settings-audit">
          <h3 className="font-semibold text-[#4A331D] mb-4 flex items-center gap-2">
            <ScrollText className="w-4 h-4" /> Audit-Log (letzte 20)
          </h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {auditLog.length === 0 && <div className="text-xs text-gray-500">Keine Einträge.</div>}
            {auditLog.map((l) => (
              <div key={l.id} className="text-xs border rounded p-2 bg-gray-50">
                <div className="flex justify-between">
                  <span className="font-medium text-[#4A331D]">{l.action}</span>
                  <span className="text-gray-500">{new Date(l.timestamp).toLocaleString('de-DE')}</span>
                </div>
                <div className="text-gray-600 mt-0.5">
                  {l.username && <span>{l.username} </span>}
                  {l.ip && <span>· IP {l.ip}</span>}
                  {l.leads_removed != null && <span> · {l.leads_removed} Leads / {l.chats_removed} Chats</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#4A331D] hover:bg-[#593B23] px-8"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
        </Button>
      </div>
    </AdminLayout>
  );
};

const Field = ({ id, label, value, onChange, placeholder, hint, type = 'text', testid }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1"
      data-testid={testid}
    />
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

export default AdminSettings;
